<template>
  <!-- 套餐商品詳情頁 -->
  <div class="scroll_flex_container product_detail_container">
    <div class="column_center_start product_info">
      <!-- 有圖片時：保持原本排版 -->
      <div
        v-if="productImage"
        ref="productImageWrapper"
        class="sticky z-10 w-full aspect-square bg-white product_img relative"
        :style="imageStickyStyle"
      >
        <div
          ref="productImageCarousel"
          class="product_image_carousel"
          @touchstart.passive="resetImageAutoPlay"
          @mousedown="resetImageAutoPlay"
          @scroll.passive="handleProductImageScroll"
        >
          <div
            v-for="(image, imageIndex) in carouselImages"
            :key="`${image.url}-${imageIndex}`"
            class="product_image_slide"
          >
            <img :src="image.url" class="w-full h-full object-cover" />
          </div>
        </div>
        
        <div class="fixed top-2 right-2 m-2">
          <div class="row_center_center" style="background-color: #DDDDDD; border-radius: 50%; padding: 2px;"
            @click="closeProductDetail">
            <text id="close" class="button-icon"
              style="color: #000; font-size: 32px; height: 32px; width: 32px">close</text>
          </div>
        </div>

        <div class="row_center_around absolute bottom-0 left-0 w-full px-4 pb-3 pt-10 bg-gradient-to-t from-black/90 to-transparent">
          <text class="text-white text-2xl font-semibold text_three_lines text-left flex-grow mr-2">
            {{ getFrontEndName(product.name) }}
          </text>
          
          <text class="text-[#FD5001] font-bold text-xl">
            ${{ totalPrice }}
          </text>
        </div>
      </div>

      <!-- 沒有圖片時：新排版 -->
      <div v-else class="sticky top-0 z-10 w-full h-[110px] bg-white product_img relative">
        <div class="fixed top-2 right-2 m-2 ">
          <div class="row_center_center" style="background-color: #DDDDDD; border-radius: 50%; padding: 2px;"
            @click="closeProductDetail">
            <text id="close" class="button-icon"
              style="color: #000; font-size: 32px; height: 32px; width: 32px">close</text>
          </div>
        </div>

        <div class="row_center_around w-full absolute bottom-0 left-0  px-4 pb-3">
          <text class="text-black text-2xl font-semibold text_three_lines text-left flex-grow mr-2">
            {{ getFrontEndName(product.name) }}
          </text>
          
          <text class="text-[#FD5001] font-bold text-xl">
            ${{ totalPrice }}
          </text>
        </div>
      </div>

      <div
        class="relative z-0 bg-white w-full px-1 py-6 -mt-4"
        style="width: 100%; flex-grow: 1; padding-bottom: 30px;"
      >
        <div v-if="productImages.length > 1" class="product_image_thumbnails">
          <button
            v-for="(image, imageIndex) in productImages"
            :key="`thumbnail-${image.url}-${imageIndex}`"
            type="button"
            class="product_image_thumbnail"
            :class="{ active: selectedImageIndex === imageIndex }"
            @click="setSelectedProductImage(imageIndex)"
          >
            <img :src="image.url" :alt="`${product.name} 圖片 ${imageIndex + 1}`" />
          </button>
        </div>
        <div class="description">
          <!-- HTML 內容渲染 -->
          <div 
            v-if="isHtmlDescription" 
            v-html="sanitizedProductDescription"
            class="html-description"
          ></div>
          
          <!-- 純文字內容渲染 -->
          <span v-else>{{ sanitizedProductDescription }}</span>
        </div>
        <!-- 固定商品 -->
        <div class="column_center_center w-full" v-if="fixedProducts.length">
          <div class="column_center_center w-full" style="width: 92%;">
            <div class="row_center_start w-full" style="padding: 15px 0">
              <text class="title">固定商品</text>
            </div>
            <productDetailSingleSubOption class="column_start_start option"
              v-for="(product, productIndex) in fixedProducts" :key="product.id" :product="product" :isSelected="true"
              :disabled="isProductSoldOut(product)"
              :editable="Boolean(product.productSpecSettings) && Boolean(product.productSpecSettings.length)"
              @openCustomizePopup="openFixedProductCustomizePopup(product, productIndex)" />
          </div>
        </div>
        <!-- 套餐子項 comboMode === 0 時顯示 -->
        <div class="column_center_center" style="width: 100%;" v-if="product.comboMode === 0 && comboItems.length">
          <div class="column_center_center" style="width: 92%;" v-for="(comboItem, comboItemIndex) in comboItems"
            :key="comboItem.id">
            <div class="column_start_stat w-full flex-wrap" style="padding: 15px 0">
              <div class="row_center_start w-full">
                <text class="title">{{ getSpecFrontEndName(comboItem) }}</text>
                <text class="subTitle" style="margin-right: 10px" v-if="comboItem.mustHave">必選</text>
              </div>
              <div class="row_center_start w-full">
                <text class="subTitle" style="margin: 0;" v-if="!comboItem.isSingleSelection">複選[最少{{
                  comboItem.multiSelectionMin }}, 最多{{ comboItem.multiSelectionMax }}]{{
                    comboItem.allowSelectManyTimes ? '(可重複選)' : '' }}</text>
              </div>

            </div>
            <div class="column_center_center w-full" v-if="!comboItem.allowSelectManyTimes">
              <!-- 單選 / 複選欄位 -->
              <productDetailSingleSubOption class="column_start_start option"
                :class="{ active: isSelected(comboItemIndex, productIndex) }"
                v-for="(product, productIndex) in comboItem.products" :key="product.id" :product="product"
                :disabled="isProductSoldOut(product)"
                :editable="isSelected(comboItemIndex, productIndex) && Boolean(product.productSpecSettings) && Boolean(product.productSpecSettings.length)"
                :isSelected="isSelected(comboItemIndex, productIndex)" cancelable
                @click="comboItem.isSingleSelection ? selectedSingleItem(comboItemIndex, productIndex) : selectedMultipleItems(comboItemIndex, productIndex)"
                @openCustomizePopup="openCustomizePopup(product, comboItemIndex)" />

            </div>

            <div class="column_center_start w-full" v-if="comboItem.allowSelectManyTimes">
              <!-- 可重複選欄位 不可客製化-->
              <productDetailMultiSubOption class="column_center_start option"
                :class="{ active: isSelected(comboItemIndex, productIndex) }"
                v-for="(product, productIndex) in comboItem.products" :key="product.id" :product="product"
                :disabled="isProductSoldOut(product)"
                @addQuantity="addOptionQuantity(comboItemIndex, productIndex)"
                @subtractQuantity="subtractOptionQuantity(comboItemIndex, productIndex)"
                :quantity="checkOptionQuantity(comboItemIndex, productIndex)" />
            </div>

            <div class="column_center_center" style="width: 100%"
              v-if="comboItem.markUpProducts && comboItem.markUpProducts.length">
              <div class="row_center_between option" @click="displaySpecialOptions(comboItemIndex)">
                <text :style="{ color: showMarkUpOptions[comboItemIndex] ? '#FD5001' : 'black' }">選擇加價項目</text>
                <img src="@/assets/black_arrow_right.png" v-if="!showMarkUpOptions[comboItemIndex]" />
                <img src="@/assets/orange_arrow_down.png" v-else />
              </div>
              <div class="column_end_start w-full"
                v-if="showMarkUpOptions[comboItemIndex] && !comboItem.allowSelectManyTimes">
                <!-- 加價項目 單選 / 複選欄位 -->
                <productDetailSingleSubOption class="column_start_start option"
                  :class="{ active: isSelected(comboItemIndex, markUpOptionIndex + comboItem.products.length) }"
                  style="width: 96%;" v-for="(markUpProduct, markUpOptionIndex) in comboItem.markUpProducts"
                  :key="markUpProduct.id" :product="markUpProduct"
                  :disabled="isProductSoldOut(markUpProduct)"
                  :editable="isSelected(comboItemIndex, markUpOptionIndex + comboItem.products.length) && Boolean(markUpProduct.productSpecSettings) && Boolean(markUpProduct.productSpecSettings.length)"
                  :isSelected="isSelected(comboItemIndex, markUpOptionIndex + comboItem.products.length)" cancelable
                  @click="comboItem.isSingleSelection ? selectedSingleItem(comboItemIndex, markUpOptionIndex + comboItem.products.length) : selectedMultipleItems(comboItemIndex, markUpOptionIndex + comboItem.products.length)"
                  @openCustomizePopup="openCustomizePopup(markUpProduct, comboItemIndex)" />
              </div>

              <div class="column_end_start w-full"
                v-if="showMarkUpOptions[comboItemIndex] && comboItem.allowSelectManyTimes">
                <!-- 加價項目 可重複選 欄位 不可客製化 -->
                <productDetailMultiSubOption class="column_center_start option"
                  :class="{ active: isSelected(comboItemIndex, markUpOptionIndex + comboItem.products.length) }"
                  style="width: 96%;" v-for="(markUpProduct, markUpOptionIndex) in comboItem.markUpProducts"
                  :key="markUpProduct.id" :product="markUpProduct"
                  :disabled="isProductSoldOut(markUpProduct)"
                  @addQuantity="addOptionQuantity(comboItemIndex, markUpOptionIndex + comboItem.products.length)"
                  @subtractQuantity="subtractOptionQuantity(comboItemIndex, markUpOptionIndex + comboItem.products.length)"
                  :quantity="checkOptionQuantity(comboItemIndex, markUpOptionIndex + comboItem.products.length)" />
              </div>
            </div>
          </div>
        </div>
        <!-- 任選子項  comboMode === 1 時顯示-->
        <div class="column_center_center w-full"
          v-if="product.comboMode === 1 && comboSubAnyProducts['products'] !== undefined && comboSubAnyProducts.products.length">
          <div class="column_center_center w-full" style="width: 92%;">
            <div class="row_center_start w-full" style="padding: 15px 0">
              <text class="title">{{ getSpecFrontEndName(comboSubAnyProducts.comboSubAnySetting) }}</text>
              <text class="subTitle" style="margin-right: 10px"
                v-if="comboSubAnyProducts.comboSubAnySetting.selectionCount">
                必選
                {{ comboSubAnyProducts.comboSubAnySetting.selectionCount }}項
                {{ comboSubAnyProducts.comboSubAnySetting.allowSelectManyTimes ? '(可重複選)' : '' }}
              </text>
            </div>
            <!-- 單選 / 複選欄位 -->
            <div class="column_center_center w-full"
              v-if="!comboSubAnyProducts.comboSubAnySetting.allowSelectManyTimes">
              <productDetailSingleSubOption class="column_start_start option"
                :class="{ active: isComboSubAnySelected(product) }" v-for="(product) in comboSubAnyProducts.products"
                :key="product.id" :product="comboSubAnyProducts.orderProducts.find(p => p.id === product.id) || product"
                :disabled="isProductSoldOut(product)"
                :editable="isComboSubAnySelected(product) && Boolean(product.productSpecSettings) && Boolean(product.productSpecSettings.length)"
                :isSelected="isComboSubAnySelected(product)" cancelable
                @click="isComboSubAnySelected(product) ? removeComboSubAnyProduct(product) : openComboSubAnyCustomizePopup(product)"
                @openCustomizePopup="openComboSubAnyCustomizePopup(comboSubAnyProducts.orderProducts.find(p => p.id === product.id) || product)" />
            </div>

            <!-- 可重複選 -->
            <div class="column_center_center w-full" v-else>
              <comboSubAnyMultiSubOption class="column_start_start option"
                :class="{ active: isComboSubAnySelected(product) }" v-for="(product) in comboSubAnyProducts.products"
                :key="product.id" :product="product" @addQuantity="openComboSubAnyCustomizePopup(product)"
                @subtractQuantity="subtractComboSubAnyOptionQuantity(product)" @removeProduct="removeComboSubAnyProduct"
                :disabled="isProductSoldOut(product)"
                :quantity="checkComboSubAnyOptionQuantity(product)"
                :orderProducts="comboSubAnyProducts.orderProducts.filter(p => p.id === product.id) || []" />
            </div>
          </div>
        </div>
      </div>
      <div class="column_start_start w-full mt-5 mb-4 px-[3%]">
        <text class="title">備註</text>
        <div class="w-full order_note" :class="noteTextAreaFocus ? 'border-[#FD5001]' : 'border-[#DDDDDD]'"
          @click="noteTextAreaFocus != noteTextAreaFocus">
          <textarea class="" :maxlength="productNoteMaxLength" :placeholder="productNotePlaceHolder" v-model="note"
            v-on:focus="noteTextAreaFocus = true" v-on:blur="noteTextAreaFocus = false"></textarea>
          <div class="w-full row_center_end">
            <p class="text-sm text-[#666666] h-5">{{ note.length }}/{{ productNoteMaxLength }}</p>
          </div>
        </div>
      </div>
    </div>
    <AddToCart v-if="!showCustomizePopup && !showFixedProductCustomizePopup && !showComboSubAnyCustomizePopup"
      :quantity="quantity" @addToCart="addToCart" @updateQuantity="updateQuantity">
    </AddToCart>
    <div>
      <CustomizeItem v-if="showCustomizePopup" @close="closeCustomizePopup">
      </CustomizeItem>
      <CustomizeItem v-if="showFixedProductCustomizePopup" @close="closeFixedProductCustomizePopup">
      </CustomizeItem>
      <CustomizeItem v-if="showComboSubAnyCustomizePopup" @close="closeComboSubAnyCustomizePopup">
      </CustomizeItem>
    </div>
  </div>
</template>
<script>
import addToCart from "@/components/store/addToCart.vue";
import commonUtilities from "@/utils/common.utils";
import customizeItem from "./customizeItem.vue";
import productDetailSingleSubOption from "@/components/store/productDetailSingleSubOption.vue";
import productDetailMultiSubOption from "@/components/store/productDetailMultiSubOption.vue";
import comboSubAnyMultiSubOption from "@/components/store/comboSubAnyMultiSubOption.vue";
import { isHtmlContent, sanitizeHtml } from "@/utils/htmlSanitizer.utils";
import { getFrontEndName, getSpecFrontEndName } from "@/utils/productName.utils";
import { mapGetters } from 'vuex'
export default {
  components: {
    AddToCart: addToCart,
    CustomizeItem: customizeItem,
    productDetailSingleSubOption,
    productDetailMultiSubOption,
    comboSubAnyMultiSubOption
  },
  props: {
    product: {
      type: Object,
      default: () => {
        return {};
      },
      required: true,
    },
  },
  data() {
    return {
      // 使用者輸入的數量, 如果輸入數量為0, 則顯示 props quantity
      quantity: 1,
      selectedImageIndex: 0,
      imageStickyTopOffset: 100,
      imageAutoPlayTimer: null,
      imageLoopResetTimer: null,
      isImageLooping: false,
      isProgrammaticImageScroll: false,
      imageScrollUnlockTimer: null,
      showMarkUpOptions: [],
      selectedOption: [],
      showCustomizePopup: false,
      showFixedProductCustomizePopup: false,
      showComboSubAnyCustomizePopup: false,
      comboItems: [],
      fixedProducts: [], // 固定商品
      comboSubAnyProducts: [], // 任選子項
      note: "",
      noteTextAreaFocus: false
    };
  },
  computed: {
    imageStickyStyle() {
      return { top: `-${this.imageStickyTopOffset}px` }
    },
    productImage() {
      return this.productImages[this.selectedImageIndex]?.url || this.productImages[0]?.url || ''
    },
    productImages() {
      const images = this.$store.getters['product/getProductImagesById'](this.product.id)
      if (images.length) {
        return images.map(image => ({
          ...image,
          url: image.image_url
        }))
      }

      const fallbackImage = this.$store.getters['product/getProductImageById'](this.product.id)
      return fallbackImage ? [{ url: fallbackImage }] : []
    },
    carouselImages() {
      if (this.productImages.length <= 1) return this.productImages

      return [
        { ...this.productImages[this.productImages.length - 1], isClone: true },
        ...this.productImages,
        { ...this.productImages[0], isClone: true }
      ]
    },
    productDescription() {
      // 使用新的 getter 來取得描述，會自動判斷使用哪個資料來源
      return this.$store.getters['product/getProductDescById'](this.product.id)
    },
    sanitizedProductDescription() {
      let rawDescription = this.$store.getters['product/getProductDescById'](this.product.id)
      
      if (!rawDescription) return ''
      
      try {
        rawDescription = rawDescription.replace(/<br><\/p>/g, '<br><br></p>')
        return sanitizeHtml(rawDescription)
      } catch (error) {
        console.warn('HTML 淨化失敗，回退到純文字模式:', error)
        return rawDescription.replace(/<[^>]*>/g, '') // 移除所有 HTML 標籤
      }
    },
    isHtmlDescription() {
      const rawDescription = this.$store.getters['product/getProductDescById'](this.product.id)
      return isHtmlContent(rawDescription)
    },
    totalPrice() {
      // 固定商品規格加價
      let fixedProductSpecPrice = 0
      for (const product of this.fixedProducts) {
        if (product.productSpecSettings.length) {
          product.productSpecSettings.forEach(spec => {
            spec.subSpecOptionSelectCount.forEach((count, index) => {
              if (count !== 0) {
                fixedProductSpecPrice += spec.specOptions[index].price * count
              }
            })
          })
        }
      }

      // 套餐子項
      const customizedTotalPrice = this.comboItems.flatMap(group =>
        (group?.productSelectCount || []).map((count, index) => ({ count, product: index > group.products.length - 1 ? group.markUpProducts[index - group.products.length] : group.products[index] }))
      ).reduce((total, { count, product }) => {
        if (count === 0 || !product.productSpecSettings || !product.productSpecSettings.length) return total;
        return total += product.productSpecSettings.reduce((total, spec) => {
          if (!spec.subSpecOptionSelectCount) return total;
          return total += spec.subSpecOptionSelectCount.reduce((total, count, index) => {
            if (count === 0 || !spec.specOptions || !spec.specOptions.length) return total;
            return total += count * spec.specOptions[index].price
          }, 0)
        }, 0);
      }, 0);

      // 套餐子項加價項目
      const markUpItemsTotalPrice = this.comboItems.flatMap(group =>
        (group?.productSelectCount || []).map((count, index) => ({ count, product: group.allProducts[index] }))
      ).reduce((total, { count, product }) => {
        if (count === 0) return total;
        return total += count * product.altAddonPrice;
      }, 0);

      // 套餐任選子項
      let comboSubAnyPrice = 0
      if (this.comboSubAnyProducts.orderProducts !== undefined && this.comboSubAnyProducts.orderProducts.length) {
        this.comboSubAnyProducts.orderProducts.forEach(product => {
          if (product.productSpecSettings.length) {
            product.productSpecSettings.forEach(spec => {
              spec.subSpecOptionSelectCount.forEach((count, index) => {
                if (count !== 0) {
                  comboSubAnyPrice += spec.specOptions[index].price * count
                }
              })
            })
          }
        })
      }

      return (this.productPrice + markUpItemsTotalPrice + customizedTotalPrice + fixedProductSpecPrice + comboSubAnyPrice) * this.quantity;
    },
    selectedComboProduct() {
      return this.$store.state.cart.selectedComboProduct
    },
    useDbData() {
      return this.$store.state.spreadsheet.useDbData
    },
    productQuantityInCart() {
      return this.$store.getters['cart/productQuantityInCart']
    },
    productNoteMaxLength() {
      return this.$store.state.order.productNoteMaxLength
    },
    productNotePlaceHolder() {
      return this.$store.state.order.productNotePlaceHolder
    },
    ...mapGetters('spreadsheet', {
      comboSubItems: 'comboSubItems',
      specifications: 'specifications',
      products: 'products',
      comboSubAny: 'comboSubAny',
      comboSubAltItems: 'comboSubAltItems'
    }),
    productPrice() {
      if (Number(this.product?.price) !== undefined || Number(this.product?.price) !== null) {
        return Number(this.product?.price)
      } else if (Number(this.product?.standardPrice) !== undefined || Number(this.product?.standardPrice) !== null) {
        return Number(this.product?.standardPrice)
      }
      return Number(this.product.price) || Number(this.product.standardPrice) || 0
    }
  },
  mounted() {
    console.log('product detail', this.product);
    if (this.product.comboMode === 0) {
      // 套餐子項
      this.comboProductsInit()
    } else if (this.product.comboMode === 1) {
      // 任選子項
      this.comboSubAnyInit()
    }
    // 固定商品
    this.fixedProductsInit()
    this.resetProductImageCarouselPosition()
    this.startImageAutoPlay()
    this.$nextTick(() => this.updateImageStickyTop())
    window.addEventListener('resize', this.updateImageStickyTop)
  },
  unmounted() {
    this.stopImageAutoPlay()
    this.clearImageLoopResetTimer()
    this.clearImageScrollUnlockTimer()
    window.removeEventListener('resize', this.updateImageStickyTop)
  },
  watch: {
    'product.id'() {
      this.selectedImageIndex = 0
      this.resetProductImageCarouselPosition()
      this.startImageAutoPlay()
      this.$nextTick(() => this.updateImageStickyTop())
    },
    productImages(images) {
      if (this.selectedImageIndex >= images.length) {
        this.selectedImageIndex = 0
      }
      this.resetProductImageCarouselPosition()
      this.startImageAutoPlay()
      this.$nextTick(() => this.updateImageStickyTop())
    }
  },
  methods: {
    getFrontEndName,
    getSpecFrontEndName,
    updateImageStickyTop() {
      const wrapper = this.$refs.productImageWrapper
      if (!wrapper) return
      const visibleHeight = 200
      this.imageStickyTopOffset = Math.max(wrapper.clientHeight - visibleHeight, 0)
    },
    startImageAutoPlay() {
      this.stopImageAutoPlay()
      if (this.productImages.length <= 1) return

      this.imageAutoPlayTimer = window.setInterval(() => {
        this.showNextProductImage()
      }, 5000)
    },
    resetImageAutoPlay() {
      if (this.productImages.length <= 1) return

      this.startImageAutoPlay()
    },
    stopImageAutoPlay() {
      if (!this.imageAutoPlayTimer) return

      window.clearInterval(this.imageAutoPlayTimer)
      this.imageAutoPlayTimer = null
    },
    clearImageLoopResetTimer() {
      if (!this.imageLoopResetTimer) return

      window.clearTimeout(this.imageLoopResetTimer)
      this.imageLoopResetTimer = null
    },
    clearImageScrollUnlockTimer() {
      if (!this.imageScrollUnlockTimer) return

      window.clearTimeout(this.imageScrollUnlockTimer)
      this.imageScrollUnlockTimer = null
    },
    lockProgrammaticImageScroll(duration = 550) {
      this.isProgrammaticImageScroll = true
      this.clearImageScrollUnlockTimer()
      this.imageScrollUnlockTimer = window.setTimeout(() => {
        this.isProgrammaticImageScroll = false
      }, duration)
    },
    showNextProductImage() {
      if (this.productImages.length <= 1) return

      const isLastImage = this.selectedImageIndex === this.productImages.length - 1
      if (isLastImage) {
        this.isImageLooping = true
        this.lockProgrammaticImageScroll()
        this.scrollProductImageToIndex(this.productImages.length + 1, 'smooth')
        this.clearImageLoopResetTimer()
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.selectedImageIndex = 0
          this.scrollProductImageToIndex(1, 'auto')
          this.isImageLooping = false
        }, 500)
        return
      }

      const nextIndex = this.selectedImageIndex + 1
      this.selectedImageIndex = nextIndex
      this.lockProgrammaticImageScroll()
      this.scrollProductImageToIndex(nextIndex + 1, 'smooth')
    },
    handleProductImageScroll() {
      const carousel = this.$refs.productImageCarousel
      if (!carousel || !carousel.clientWidth) return

      const imageIndex = Math.round(carousel.scrollLeft / carousel.clientWidth)
      if (this.productImages.length <= 1) {
        this.selectedImageIndex = imageIndex
        return
      }

      if (this.isImageLooping || this.isProgrammaticImageScroll) return

      if (imageIndex === 0) {
        this.selectedImageIndex = this.productImages.length - 1
        this.clearImageLoopResetTimer()
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.scrollProductImageToIndex(this.productImages.length, 'auto')
        }, 100)
        return
      }

      if (imageIndex === this.productImages.length + 1) {
        this.selectedImageIndex = 0
        this.clearImageLoopResetTimer()
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.scrollProductImageToIndex(1, 'auto')
        }, 100)
        return
      }

      this.selectedImageIndex = imageIndex - 1
    },
    setSelectedProductImage(index) {
      this.resetImageAutoPlay()

      if (this.selectedImageIndex === this.productImages.length - 1 && index === 0 && this.productImages.length > 1) {
        this.showNextProductImage()
        return
      }

      if (this.selectedImageIndex === 0 && index === this.productImages.length - 1 && this.productImages.length > 1) {
        this.selectedImageIndex = index
        this.isImageLooping = true
        this.lockProgrammaticImageScroll()
        this.scrollProductImageToIndex(0, 'smooth')
        this.clearImageLoopResetTimer()
        this.imageLoopResetTimer = window.setTimeout(() => {
          this.scrollProductImageToIndex(this.productImages.length, 'auto')
          this.isImageLooping = false
        }, 500)
        return
      }

      this.selectedImageIndex = index
      this.lockProgrammaticImageScroll()
      this.$nextTick(() => {
        this.scrollProductImageToIndex(this.productImages.length > 1 ? index + 1 : index, 'smooth')
      })
    },
    resetProductImageCarouselPosition() {
      this.$nextTick(() => {
        this.scrollProductImageToIndex(this.productImages.length > 1 ? this.selectedImageIndex + 1 : this.selectedImageIndex, 'auto')
      })
    },
    scrollProductImageToIndex(index, behavior = 'smooth') {
      const carousel = this.$refs.productImageCarousel
      if (!carousel) return

      carousel.scrollTo({
        left: carousel.clientWidth * index,
        behavior
      })
    },
    comboSubAnyInit() {
      // comboSubAnySetting 提供任選子項的設定
      // products 提供任選子項的商品
      // productSelectCount 提供記住對應 index 的商品選擇數量 
      // 訂單需要 comboSubAnySetting & orderProducts
      if (!this.product.comboSubAnySetting) return
      const comboSubAnyID = this.product.comboSubAnySetting.comboSubAnyID
      const comboSubAny = this.comboSubAny.find(item => item.comboSubAnyID === comboSubAnyID)
      const productSelectCount = comboSubAny.productIDs.map(() => 0)
      const products = comboSubAny.productIDs.map(id => {
        const product = structuredClone(this.getProductById(id))
        const productSpecSettings = product.productSpecSettings.map(subSpec => {
          const subSpecID = String(subSpec.subSpecID || '').trim()
          const specIndex = this.specifications.findIndex(spec => String(spec.id || '').trim() === subSpecID)
          let spec = {}
          if (specIndex !== -1) {
            spec = this.specifications[specIndex]
            // spec.specOptions 有多少選項 就在 subSpecOptionSelectCount 設定多少個 0
            subSpec['subSpecOptionSelectCount'] = spec?.specOptions.map(() => 0) || []
          }
          return { ...subSpec, ...spec }
        }).filter(spec => spec.enableForProductComboSpec === true)
        return { ...product, productSpecSettings }
      })
      this.comboSubAnyProducts = { products, comboSubAnySetting: this.product.comboSubAnySetting, productSelectCount, orderProducts: [] }
      console.log('comboSubAny', this.comboSubAnyProducts)
    },
    fixedProductsInit() {
      if (this.product.productIDs === undefined) return
      this.fixedProducts = this.product.productIDs.map(id => {
        const product = structuredClone(this.getProductById(id))
        const productSpecSettings = this.productSpecSettingsInit(product)
        return { ...product, productSpecSettings }
      })
      console.log('fixedProducts', this.fixedProducts)
    },
    productSpecSettingsInit(product) {
      return product.productSpecSettings.map(subSpec => {
        const subSpecID = String(subSpec.subSpecID || '').trim()
        const specIndex = this.specifications.findIndex(spec => String(spec.id || '').trim() === subSpecID)
        let spec = {}
        if (specIndex !== -1) {
          spec = this.specifications[specIndex]
          // spec.specOptions 有多少選項 就在 subSpecOptionSelectCount 設定多少個 0
          subSpec['subSpecOptionSelectCount'] = spec?.specOptions.map(() => 0) || []
        }
        return { ...subSpec, ...spec }
      }).filter(spec => spec.enableForProductComboSpec === true)
    },
    getProductById(id) {
      return this.products.find(product => product.id === id)
    },
    getComboOptionProduct(specIndex, subSpecIndex) {
      const group = this.comboItems[specIndex]
      if (!group) return null
      if (subSpecIndex < group.products.length) return group.products[subSpecIndex]
      return group.markUpProducts[subSpecIndex - group.products.length]
    },
    isProductSoldOut(product) {
      return this.useDbData && Number(product?.stockMode) === 1 && product?.isStockSoldOut
    },
    getRemainingStockQuantity(product) {
      if (!this.useDbData || Number(product?.stockMode) !== 1) return Infinity
      return Math.max(Number(product.stockCount || 0) - this.productQuantityInCart(product.id), 0)
    },
    formatStockLimitMessage(product, availableQuantity = null) {
      const currentCartQuantity = this.productQuantityInCart(product.id)
      const currentAvailableQuantity = availableQuantity === null
        ? this.getRemainingStockQuantity(product)
        : availableQuantity
      return `${getFrontEndName(product.name)}剩餘數量不足\n目前購物車：${currentCartQuantity}\n本次最多可加：${Math.max(currentAvailableQuantity, 0)}`
    },
    comboProductsInit() {
      if (this.product.comboSubSettings === undefined) return
      this.comboItems = structuredClone(this.product.comboSubSettings).map(subSpec => {
        const comboItem = structuredClone(this.comboSubItems.find(comboSubItem => comboSubItem.id === subSpec.subSpecID)) || null
        if (comboItem) {
          // 有替換子項 id 的話需將替換子項的商品id 都加入 comboItem.productIDs
          if (comboItem.comboSubAltID) {
            const comboSubAlt = structuredClone(this.comboSubAltItems.find(item => item.id === comboItem.comboSubAltID)) || null
            if (comboSubAlt) {
              comboItem.productIDs = [...comboItem.productIDs, ...comboSubAlt.productIDs]
            }
            subSpec['comboSubAlt'] = { ...comboSubAlt }
          }
          subSpec['comboSub'] = { ...comboItem }
          // 移除重複的商品 id
          comboItem.productIDs = [...new Set(comboItem.productIDs)]
          // productIDs 有多少選項 就在 productSelectCount 設定多少個 0
          subSpec['productSelectCount'] = comboItem?.productIDs.map(() => 0) || []
          const products = []
          const markUpProducts = []
          const allProducts = []
          for (const productId of comboItem.productIDs) {
            const product = structuredClone(this.getProductById(productId))
            product.productSpecSettings = this.productSpecSettingsInit(product)
            allProducts.push(product)
            // compute if each product in comboItem has alter price difference, if so,push into markUpProducts array, else push into products array
            if (comboItem.altBasePrice && comboItem.altBasePrice !== 0 && Number(product.price) - Number(comboItem.altBasePrice) > 0) {
              product['altAddonPrice'] = Number(product.price) - Number(comboItem.altBasePrice)
              markUpProducts.push(product)
            } else {
              product['altAddonPrice'] = 0
              products.push(product)
            }
          }
          subSpec['allProducts'] = allProducts
          subSpec['products'] = products
          subSpec['markUpProducts'] = markUpProducts
        }
        // 將選擇加價項目欄位展開設為 false
        this.showMarkUpOptions.push(false)
        return { ...subSpec }
      })

      console.log('comboItems', this.comboItems)
    },
    checkOptionQuantity(specIndex, subSpecIndex) {
      // 檢查選項是否有存在選項中
      return this.comboItems[specIndex].productSelectCount[subSpecIndex]
    },
    checkComboSubAnyOptionQuantity(product) {
      return this.comboSubAnyProducts.orderProducts.filter(p => p.id === product.id).length
    },
    addComboSubAnyOptionQuantity(index) {
      this.comboSubAnyProducts.productSelectCount[index]++
    },
    subtractComboSubAnyOptionQuantity(product) {
      const index = this.comboSubAnyProducts.orderProducts.map(p => p.id).lastIndexOf(product.id)
      this.comboSubAnyProducts.orderProducts.splice(index, 1)
    },
    addOptionQuantity(specIndex, subSpecIndex) {
      if (this.isProductSoldOut(this.getComboOptionProduct(specIndex, subSpecIndex))) {
        commonUtilities.createToast("warning", "商品已售完", "")
        return
      }
      const optionProduct = this.getComboOptionProduct(specIndex, subSpecIndex)
      const availableQuantity = this.getRemainingStockQuantity(optionProduct) - this.getRequestedProductQuantity(optionProduct)
      if (Number(this.quantity || 0) > availableQuantity) {
        commonUtilities.createToast("warning", this.formatStockLimitMessage(optionProduct, availableQuantity), "")
        return
      }
      // 複選 ＆ 可重複選則加點數量
      // 檢查是否超過最大選擇數
      const currentSpecSelectCount = this.comboItems[specIndex].productSelectCount.reduce((acc, el) => acc + el, 0)
      const currentSpecSelectionMax = this.comboItems[specIndex].multiSelectionMax
      if (currentSpecSelectCount >= currentSpecSelectionMax) {
        commonUtilities.createToast("warning", `最多選擇${currentSpecSelectionMax}個`, "")
        return
      }
      this.comboItems[specIndex].productSelectCount[subSpecIndex]++
    },
    subtractOptionQuantity(specIndex, subSpecIndex) {
      const currentSpecSelectCount = this.comboItems[specIndex].productSelectCount.reduce((acc, el) => acc + el, 0)
      // 如果加點數量為 0 或是該選項沒有被選中，則不做任何事
      if (currentSpecSelectCount === 0 || this.comboItems[specIndex].productSelectCount[subSpecIndex] === 0) {
        return
      }
      // 如果加點數量為1，則移除選項
      if (currentSpecSelectCount == 1) {
        this.comboItems[specIndex].productSelectCount[subSpecIndex]--
        return
      }
      // 如果加點數量大於1，則數量減一，並移除其中一個選項
      if (currentSpecSelectCount > 1) {
        this.comboItems[specIndex].productSelectCount[subSpecIndex]--
      }
    },
    toBoolean(value) {
      return (/true/).test(value)
    },
    closeComboSubAnyCustomizePopup() {
      if (this.selectedComboProduct === null) {
        this.showComboSubAnyCustomizePopup = false;
        return;
      }
      let { product: selectedProduct } = this.selectedComboProduct;

      if (!selectedProduct) {
        this.showComboSubAnyCustomizePopup = false;
        return;
      }

      // 將客製化的商品更新至 products list 中
      if (!this.comboSubAnyProducts.comboSubAnySetting.allowSelectManyTimes) {
        // 單選 / 複選欄位
        // 如果 orderProducts 中已經有商品，則更新商品
        if (this.comboSubAnyProducts.orderProducts.find(p => p.id === selectedProduct.id)) {
          const productIndex = this.comboSubAnyProducts.orderProducts.findIndex(p => p.id === selectedProduct.id)
          this.comboSubAnyProducts.orderProducts[productIndex] = selectedProduct
        } else {
          this.comboSubAnyProducts.orderProducts.push(selectedProduct)
        }
      } else {
        // 可重複選
        this.comboSubAnyProducts.orderProducts.push(selectedProduct)
      }
      this.$store.commit("cart/setSelectedComboProduct", null);
      this.showComboSubAnyCustomizePopup = false;
    },
    closeFixedProductCustomizePopup() {
      if (this.selectedComboProduct === null) {
        this.showFixedProductCustomizePopup = false;
        return;
      }
      let { product: selectedProduct, groupIndex } = this.selectedComboProduct;

      if (!selectedProduct) {
        this.showFixedProductCustomizePopup = false;
        return;
      }

      // 將客製化的商品更新至 products list 中
      if (selectedProduct.id === this.fixedProducts[groupIndex].id) {
        this.fixedProducts[groupIndex] = selectedProduct;
      }
      this.$store.commit("cart/setSelectedComboProduct", null);
      this.showFixedProductCustomizePopup = false;
    },
    closeCustomizePopup() {
      if (this.selectedComboProduct === null) {
        this.showCustomizePopup = false;
        return;
      }
      let { product: selectedProduct, groupIndex } = this.selectedComboProduct;

      if (!selectedProduct) {
        this.showCustomizePopup = false;
        return;
      }

      // 將客製化的商品更新至 products list 中

      if (this.comboItems[groupIndex].products.find(product => product.id === selectedProduct.id)) {
        const productIndex = this.comboItems[groupIndex].products.findIndex(product => product.id === selectedProduct.id)
        this.comboItems[groupIndex].products[productIndex] = selectedProduct
      } else {
        const productIndex = this.comboItems[groupIndex].markUpProducts.findIndex(product => product.id === selectedProduct.id)
        this.comboItems[groupIndex].markUpProducts[productIndex] = selectedProduct
      }
      this.$store.commit("cart/setSelectedComboProduct", null);
      this.showCustomizePopup = false;
    },
    openComboSubAnyCustomizePopup(product) {
      if (this.isProductSoldOut(product)) {
        commonUtilities.createToast("warning", "商品已售完", "")
        return
      }
      const isEditingSelectedProduct = !this.comboSubAnyProducts.comboSubAnySetting.allowSelectManyTimes && this.isComboSubAnySelected(product)
      const availableQuantity = this.getRemainingStockQuantity(product) - this.getRequestedProductQuantity(product)
      if (!isEditingSelectedProduct && Number(this.quantity || 0) > availableQuantity) {
        commonUtilities.createToast("warning", this.formatStockLimitMessage(product, availableQuantity), "")
        return
      }
      if (product.productSpecSettings && !product.productSpecSettings.length) {
        // 無規格的商品直接加入
        this.comboSubAnyProducts.orderProducts.push(product)
        return
      }
      // 將商品資料傳入
      event.stopPropagation();
      this.$store.commit("cart/setSelectedComboProduct", {
        product: structuredClone(product)
      });
      this.showComboSubAnyCustomizePopup = true
    },
    openCustomizePopup(product, groupIndex) {
      if (this.isProductSoldOut(product)) {
        commonUtilities.createToast("warning", "商品已售完", "")
        return
      }
      // 將商品資料傳入
      event.stopPropagation();
      this.$store.commit("cart/setSelectedComboProduct", {
        product: { ...product },
        groupIndex: groupIndex
      });
      this.showCustomizePopup = true
    },
    openFixedProductCustomizePopup(product, productIndex) {
      if (this.isProductSoldOut(product)) {
        commonUtilities.createToast("warning", "商品已售完", "")
        return
      }
      // 將商品資料傳入
      event.stopPropagation();
      this.$store.commit("cart/setSelectedComboProduct", {
        product: { ...product },
        groupIndex: productIndex
      });
      this.showFixedProductCustomizePopup = true
    },
    displaySpecialOptions(index) {
      this.showMarkUpOptions[index] = !this.showMarkUpOptions[index];
    },
    removeComboSubAnyProduct(product, index) {
      if (index === undefined) {
        index = this.comboSubAnyProducts.orderProducts.findIndex(p => p.id === product.id)
      }
      const productIndex = this.comboSubAnyProducts.orderProducts.findIndex(p => p.id === product.id)
      this.comboSubAnyProducts.orderProducts.splice(index + productIndex, 1)
    },
    selectedSingleSubAny(index) {
      // 任選子項單選
      const hasSelected = this.comboSubAnyProducts.productSelectCount[index] > 0

      if (!hasSelected) {
        // 將選到的 productSelectCount[index] 設為 1，其餘設為0
        this.comboSubAnyProducts.productSelectCount = this.comboSubAnyProducts.productSelectCount.map((el, i) => {
          return i === index ? 1 : 0
        })
      } else {
        this.comboSubAnyProducts.productSelectCount[index] = 0
      }
    },
    selectedMultipleSubAny(index) {
      // 複選
      // 檢查選項是否已經被選中
      const hasSelected = this.comboSubAnyProducts.productSelectCount[index] > 0
      const totalCount = this.comboSubAnyProducts.productSelectCount.reduce((acc, el) => acc + el, 0)
      const maxSelectedCounts = this.comboSubAnyProducts.comboSubAnySetting.selectionCount

      if (!hasSelected) {
        // 檢查是否超過最大選擇數
        if (totalCount >= maxSelectedCounts) {
          commonUtilities.createToast("warning", `最多選擇${totalCount}個`, "")
          return
        }
        // 如果沒有被選中，則添加到選中的選項中
        this.comboSubAnyProducts.productSelectCount[index]++
      } else {
        // 如果已經被選中，則從選中的選項中移除
        this.comboSubAnyProducts.productSelectCount[index]--
        // 移除客製化選項數量
        const productSpecSettings = this.comboSubAnyProducts.products[index].productSpecSettings
        for (let subSpec of productSpecSettings) {
          if (subSpec.subSpecOptionSelectCount === undefined) return
          subSpec.subSpecOptionSelectCount = subSpec.subSpecOptionSelectCount.map(() => 0)
        }
        this.comboSubAnyProducts.products[index].productSpecSettings = productSpecSettings
      }
    },
    selectedSingleItem(specIndex, subSpecIndex) {
      if (this.isProductSoldOut(this.getComboOptionProduct(specIndex, subSpecIndex))) {
        commonUtilities.createToast("warning", "商品已售完", "")
        return
      }
      const optionProduct = this.getComboOptionProduct(specIndex, subSpecIndex)
      // 單選
      const hasSelected = this.comboItems[specIndex].productSelectCount[subSpecIndex] > 0;

      if (!hasSelected) {
        const currentSelectedQuantity = this.comboItems[specIndex].productSelectCount[subSpecIndex] * this.quantity
        const availableQuantity = this.getRemainingStockQuantity(optionProduct) - this.getRequestedProductQuantity(optionProduct) + currentSelectedQuantity
        if (Number(this.quantity || 0) > availableQuantity) {
          commonUtilities.createToast("warning", this.formatStockLimitMessage(optionProduct, availableQuantity), "")
          return
        }
        // 將選到的 productSelectCount[subSpecIndex] 設為 1，其餘設為0
        this.comboItems[specIndex].productSelectCount = this.comboItems[specIndex].productSelectCount.map((el, i) => {
          return i === subSpecIndex ? 1 : 0
        })
      } else {
        this.comboItems[specIndex].productSelectCount[subSpecIndex] = 0
      }
    },
    selectedMultipleItems(specIndex, subSpecIndex) {
      if (this.isProductSoldOut(this.getComboOptionProduct(specIndex, subSpecIndex))) {
        commonUtilities.createToast("warning", "商品已售完", "")
        return
      }
      const optionProduct = this.getComboOptionProduct(specIndex, subSpecIndex)
      // 複選
      // 檢查選項是否已經被選中
      const hasSelected = this.comboItems[specIndex].productSelectCount[subSpecIndex] > 0;
      const totalCount = this.comboItems[specIndex].productSelectCount.reduce((acc, el) => acc + el, 0)

      if (!hasSelected) {
        const availableQuantity = this.getRemainingStockQuantity(optionProduct) - this.getRequestedProductQuantity(optionProduct)
        if (Number(this.quantity || 0) > availableQuantity) {
          commonUtilities.createToast("warning", this.formatStockLimitMessage(optionProduct, availableQuantity), "")
          return
        }
        // 檢查是否超過最大選擇數
        if (totalCount >= this.comboItems[specIndex].multiSelectionMax) {
          commonUtilities.createToast("warning", `最多選擇${this.comboItems[specIndex].multiSelectionMax}個`, "")
          return
        }
        // 如果沒有被選中，則添加到選中的選項中
        this.comboItems[specIndex].productSelectCount[subSpecIndex]++
      } else {
        // 如果已經被選中，則從選中的選項中移除
        this.comboItems[specIndex].productSelectCount[subSpecIndex]--
        // 移除客製化選項數量
        if (subSpecIndex > this.comboItems[specIndex].products.length - 1) {
          // 如果 subSpecIndex 大於 this.comboItems[specIndex].products.length，則為加價項目
          const productSpecSettings = this.comboItems[specIndex].markUpProducts[subSpecIndex - this.comboItems[specIndex].products.length].productSpecSettings
          for (let subSpec of productSpecSettings) {
            if (subSpec.subSpecOptionSelectCount === undefined) return
            subSpec.subSpecOptionSelectCount = subSpec.subSpecOptionSelectCount.map(() => 0)
          }
          this.comboItems[specIndex].markUpProducts[subSpecIndex - this.comboItems[specIndex].products.length].productSpecSettings = productSpecSettings
        } else {
          const productSpecSettings = this.comboItems[specIndex].products[subSpecIndex].productSpecSettings
          for (let subSpec of productSpecSettings) {
            if (subSpec.subSpecOptionSelectCount === undefined) return
            subSpec.subSpecOptionSelectCount = subSpec.subSpecOptionSelectCount.map(() => 0)
          }
          this.comboItems[specIndex].products[subSpecIndex].productSpecSettings = productSpecSettings
        }
      }
    },
    isSelected(specIndex, subSpecIndex) {
      return this.comboItems[specIndex].productSelectCount[subSpecIndex] > 0
    },
    isComboSubAnySelected(product) {
      return this.comboSubAnyProducts.orderProducts.find(p => p.id === product.id)
    },
    closeProductDetail() {
      this.$emit("close");
    },
    updateQuantity(value) {
      this.quantity = value
    },
    getRequestedStockProducts() {
      const requestedProductsById = {}
      const addProduct = (product, count = 1) => {
        if (!this.useDbData || Number(product?.stockMode) !== 1) return
        if (!requestedProductsById[product.id]) {
          requestedProductsById[product.id] = {
            product,
            quantity: 0
          }
        }
        requestedProductsById[product.id].quantity += (Number(count) || 0) * this.quantity
      }

      this.fixedProducts.forEach(product => addProduct(product, 1))

      this.comboItems.forEach(group => {
        const products = (group.products || []).concat(group.markUpProducts || [])
        products.forEach((product, index) => {
          addProduct(product, group.productSelectCount?.[index] || 0)
        })
      })

      if (this.comboSubAnyProducts.orderProducts?.length) {
        this.comboSubAnyProducts.orderProducts.forEach(product => addProduct(product, 1))
      }

      return Object.values(requestedProductsById)
    },
    getRequestedProductQuantity(product) {
      if (!product?.id) return 0
      const requestedProduct = this.getRequestedStockProducts().find(item => item.product.id === product.id)
      return requestedProduct?.quantity || 0
    },
    validateStockQuantities() {
      this.getRequestedStockProducts().forEach(({ product, quantity }) => {
        const stockCount = Number(product.stockCount || 0)
        const currentCartQuantity = this.productQuantityInCart(product.id)
        if (currentCartQuantity + quantity > stockCount) {
          throw this.formatStockLimitMessage(product)
        }
      })
    },
    cartValidator() {
      if (this.quantity == 0) {
        throw "請選擇數量"
      }
      this.validateStockQuantities()
      // 檢查固定商品中是否有必選規格
      if (this.fixedProducts.length) {
        for (const product of this.fixedProducts) {
          if (product.productSpecSettings === undefined || !product.productSpecSettings.length) continue
          for (const spec of product.productSpecSettings) {
            if (spec.subSpecOptionSelectCount === undefined) {
              throw `${getFrontEndName(product.name)} ${spec.frontEndDisplayName}尚未選擇`
            }
            const currentOptionsCount = spec.subSpecOptionSelectCount.reduce((acc, el) => acc + el, 0)
            // 如果是必選
            if (spec.mustHave && currentOptionsCount === 0) {
              throw `${getFrontEndName(product.name)} ${spec.frontEndDisplayName}尚未選擇`
            }
            // 如果是單選並且是必選
            if (spec.mustHave && spec.isSingleSelection && currentOptionsCount !== 1) {
              throw `${getFrontEndName(product.name)} ${spec.frontEndDisplayName}尚未選擇`
            }
            // 如果是複選 or 可重複選，加總各項的 quantity 是否符合 minSelect, maxSelect
            if (!spec.isSingleSelection) {
              if (currentOptionsCount < spec.multiSelectionMin) {
                throw `${spec.name}尚未選擇`
              }
              if (currentOptionsCount > spec.multiSelectionMax) {
                throw `${spec.name}選擇數量超過最大值`
              }
            }
          }
        }
      }

      // 檢查套餐子項
      if (this.comboItems.length) {
        for (const option of this.comboItems) {
          const currentProductCount = option.productSelectCount.reduce((acc, el) => acc + el, 0)

          // 如果是必選
          if (option.mustHave) {
            if (currentProductCount === 0) {
              throw `${option.frontEndDisplayName}尚未選擇`
            }
            // 如果是單選並且是必選
            if (option.isSingleSelection && currentProductCount !== 1) {
              throw `${option.frontEndDisplayName}尚未選擇`
            }
          }
          // 如果是複選 or 可重複選，加總各項的 quantity 是否符合 minSelect, maxSelect
          if (!option.isSingleSelection) {
            if (currentProductCount < option.multiSelectionMin) {
              throw `${option.frontEndDisplayName}尚未選擇`
            }
            if (currentProductCount > option.multiSelectionMax) {
              throw `${option.frontEndDisplayName}選擇數量超過最大值`
            }
          }

          // 檢查選擇商品中各項是否有客製化選項
          option.productSelectCount.forEach((count, index) => {
            if (count === 0) return
            if (index < option.products.length) {
              // 不是加價項目
              if (option.products[index].productSpecSettings === undefined || !option.products[index].productSpecSettings.length) return
              for (const spec of option.products[index].productSpecSettings) {
                // 如果缺少規格選項但不是必選 跳過不檢查
                if (spec.subSpecOptionSelectCount === undefined && !spec.mustHave) {
                  continue
                }
                // 檢查是否有規格選項
                if (spec.subSpecOptionSelectCount === undefined && spec.mustHave) {
                  throw `${getFrontEndName(option.products[index].name)} ${spec.frontEndDisplayName}尚未選擇`
                }
                const productName = getFrontEndName(option.products[index].name)
                const currentOptionsCount = spec.subSpecOptionSelectCount.reduce((acc, el) => acc + el, 0)
                // 如果是必選
                if (spec.mustHave && currentOptionsCount === 0) {
                  throw `${productName} ${spec.frontEndDisplayName}尚未選擇`
                }
                // 如果是單選並且是必選
                if (spec.mustHave && spec.isSingleSelection && currentOptionsCount !== 1) {
                  throw `${productName} ${spec.frontEndDisplayName}尚未選擇`
                }
                // 如果是複選 or 可重複選，加總各項的 quantity 是否符合 minSelect, maxSelect
                if (!spec.isSingleSelection) {
                  if (currentOptionsCount < spec.multiSelectionMin) {
                    throw `${spec.name}尚未選擇`
                  }
                  if (currentOptionsCount > spec.multiSelectionMax) {
                    throw `${spec.name}選擇數量超過最大值`
                  }
                }
              }
            } else {
              // 加價項目
              if (option.markUpProducts[index - option.products.length].productSpecSettings === undefined || !option.markUpProducts[index - option.products.length].productSpecSettings.length) return
              for (const spec of option.markUpProducts[index - option.products.length].productSpecSettings) {
                if (spec.subSpecOptionSelectCount === undefined) {
                  throw `${getFrontEndName(option.markUpProducts[index - option.products.length].name)} ${spec.frontEndDisplayName}尚未選擇`
                }
                const currentOptionsCount = spec.subSpecOptionSelectCount.reduce((acc, el) => acc + el, 0)
                // 如果是必選
                if (spec.mustHave && currentOptionsCount === 0) {
                  throw `${getFrontEndName(option.markUpProducts[index - option.products.length].name)} ${spec.frontEndDisplayName}尚未選擇`
                }
                // 如果是單選並且是必選
                if (spec.mustHave && spec.isSingleSelection && currentOptionsCount !== 1) {
                  throw `${getFrontEndName(option.markUpProducts[index - option.products.length].name)} ${spec.frontEndDisplayName}尚未選擇`
                }
                // 如果是複選 or 可重複選，加總各項的 quantity 是否符合 minSelect, maxSelect
                if (!spec.isSingleSelection) {
                  if (currentOptionsCount < spec.multiSelectionMin) {
                    throw `${spec.name}尚未選擇`
                  }
                  if (currentOptionsCount > spec.multiSelectionMax) {
                    throw `${spec.name}選擇數量超過最大值`
                  }
                }
              }
            }
          });
        }
      }

      // 檢查任選子項
      if (this.comboSubAnyProducts.products && this.comboSubAnyProducts.products.length) {
        // 檢查任選子項是否有達到必選數量
        const comboSubAnySelectionCount = this.comboSubAnyProducts.comboSubAnySetting.selectionCount
        const currentComboSubAnySelectionCount = this.comboSubAnyProducts.orderProducts.length
        // 如果目前選取數量 % 必選數量 !== 0，需提示未達足夠數量
        if (currentComboSubAnySelectionCount % comboSubAnySelectionCount !== 0) {
          throw `${this.comboSubAnyProducts.comboSubAnySetting.frontEndDisplayName} 未達足夠數量`
        }

        const comboSubAnySettingName = this.comboSubAnyProducts.comboSubAnySetting.frontEndDisplayName
        if (this.comboSubAnyProducts.comboSubAnySetting.allowSelectManyTimes) return
        // 任選子項在選擇增加數量時就已經檢查規格
        this.comboSubAnyProducts.orderProducts.forEach((product) => {
          // 檢查選擇商品規格
          for (const spec of product.productSpecSettings) {
            if (spec.subSpecOptionSelectCount === undefined) {
              throw `${comboSubAnySettingName} ${product.name} ${spec.frontEndDisplayName}尚未選擇`
            }
            const currentOptionsCount = spec.subSpecOptionSelectCount.reduce((acc, el) => acc + el, 0)
            // 如果是必選
            if (spec.mustHave && currentOptionsCount === 0) {
              throw `${comboSubAnySettingName} ${product.name} ${spec.frontEndDisplayName}尚未選擇`
            }
            // 如果是單選並且是必選
            if (spec.mustHave && spec.isSingleSelection && currentOptionsCount !== 1) {
              throw `${comboSubAnySettingName} ${product.name} ${spec.frontEndDisplayName}尚未選擇`
            }
            // 如果是複選 or 可重複選，加總各項的 quantity 是否符合 minSelect, maxSelect
            if (!spec.isSingleSelection) {
              if (currentOptionsCount < spec.multiSelectionMin) {
                throw `${comboSubAnySettingName} ${spec.name}尚未選擇`
              }
              if (currentOptionsCount > spec.multiSelectionMax) {
                throw `${comboSubAnySettingName} ${spec.name}選擇數量超過最大值`
              }
            }
          }
        })
      }
    },
    addToCart() {
      try {
        this.cartValidator()
      } catch (error) {
        commonUtilities.createToast("warning", error, "")
        return
      }

      const trimmedComboItems = this.comboItems.map(group => {
        const allProducts = group.allProducts.map(p => ({
          id: p.id, name: p.name, altAddonPrice: p.altAddonPrice || 0
        }))
        const products = group.products.map((p, i) =>
          group.productSelectCount[i] === 0
            ? { id: p.id, name: p.name, altAddonPrice: p.altAddonPrice || 0, productSpecSettings: [] }
            : p
        )
        const markUpProducts = group.markUpProducts.map((p, i) =>
          group.productSelectCount[group.products.length + i] === 0
            ? { id: p.id, name: p.name, altAddonPrice: p.altAddonPrice || 0, productSpecSettings: [] }
            : p
        )
        return { ...group, allProducts, products, markUpProducts }
      })

      const orderItem = {
        product: this.product,
        quantity: this.quantity,
        isCombo: true,
        settings: trimmedComboItems,
        // 固定餐點
        fixedProducts: this.fixedProducts,
        // 套餐子項任選
        comboSubItem: trimmedComboItems,
        // 任選子項
        comboSubAnyProducts: this.comboSubAnyProducts,
        note: this.note
      }
      console.log('加入購物車', orderItem)
      this.$store.dispatch("cart/addToCart", orderItem);
      this.$emit("close");
    }
  },
};
</script>
<style lang="scss" scoped>
@import '@/static/css/html-content-fix.css';

.product_detail_container {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.product_info {
  width: 100%;
  flex-grow: 1;
  border-radius: 6px 6px 0 0;
  background-color: #fff;
  // flex-shrink: 0;
  overflow-y: auto;
}

.product_img {
  width: 100%;
  flex-shrink: 0;
  font-family: PingFang SC;
  font-weight: 500;
  font-size: 24px;
}

.product_image_carousel {
  display: flex;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.product_image_carousel::-webkit-scrollbar {
  display: none;
}

.product_image_slide {
  width: 100%;
  height: 100%;
  flex: 0 0 100%;
  scroll-snap-align: start;
}

.product_image_thumbnails {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 70px;
  padding: 10px 12px;
  overflow-x: auto;
  background-color: #fff;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

.product_image_thumbnails::-webkit-scrollbar {
  display: none;
}

.product_image_thumbnail {
  width: 50px;
  height: 50px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  flex: 0 0 50px;
  background-color: #f5f5f5;
}

.product_image_thumbnail.active {
  border-color: #FD5001;
}

.product_image_thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.description {
  border-bottom: 1px dotted #000;
  padding-bottom: 15px;
  width: 92%;
  margin: 0 auto;
  font-size: 14px;
  font-weight: 400;
  color: #666666;

  .html-description {
    // 基本文字樣式
    line-height: 1.6;
    word-wrap: break-word;

    b, strong {
      font-weight: bold;
    }
    
    i, em {
      font-style: italic;
    }
    
    u {
      text-decoration: underline;
    }
    
    span {
      // 支援 style 屬性的內聯樣式
      // 允許的 CSS 屬性會通過 HTML 淨化工具處理
      display: inline;
    }

    // 響應式設計支援
    @media (max-width: 768px) {
      font-size: 13px;
    }
  }
}

.subTitle {
  font-size: 14px;
  font-weight: 400;
  font-family: PingFang SC;
  color: #d60c0a;
  margin-left: 10px;
}

.option {
  width: 100%;
  padding: 10px 6%;
  background-color: #eeeeee;
  font-size: 16px;
  font-weight: 400;
  font-family: PingFang SC;
  margin-bottom: 10px;
  border-radius: 6px;

  img {
    width: 32px;
    height: 32px;
  }
}

.option.active {
  border: #FD4F02 2px solid;
  background-color: #fff;
  color: #FD4F02;
}

.customized_options {
  color: #666666;
  font-size: 14px;
  margin-top: 10px;
  width: 100%;
}


.order_note {
  width: 100%;
  padding: 10px 5%;
  height: 120px;
  border-width: 1px;
  border-style: solid;
  border-radius: 12px;
  margin-top: 10px;
  font-size: 18px;
  display: flex;
  flex-direction: column;

  textArea {
    width: 100%;
    flex-grow: 1;
  }

  textArea:focus {
    outline: none;
  }
}
</style>
